import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { SessionUser } from '@notion-training/shared';
import { api, ApiError } from './api.js';

type AuthState =
  | { status: 'loading' }
  | { status: 'signed-out'; reason?: 'not_a_getvim_account' | 'unauthenticated' }
  | { status: 'signed-in'; user: SessionUser };

type AuthContextValue = {
  state: AuthState;
  signIn: (googleCredential: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  // Bootstrap: ask the server who I am.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get<{ user: SessionUser }>('/api/me');
        if (!cancelled) setState({ status: 'signed-in', user: me.user });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setState({ status: 'signed-out' });
        } else {
          setState({ status: 'signed-out' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      async signIn(googleCredential: string) {
        try {
          const res = await api.post<{ user: SessionUser }>('/api/auth/google', {
            credential: googleCredential,
          });
          setState({ status: 'signed-in', user: res.user });
        } catch (err) {
          if (err instanceof ApiError && err.status === 403 && err.message === 'not_a_getvim_account') {
            setState({ status: 'signed-out', reason: 'not_a_getvim_account' });
            return;
          }
          setState({ status: 'signed-out' });
          throw err;
        }
      },
      async signOut() {
        await api.post('/api/auth/logout');
        setState({ status: 'signed-out' });
      },
    }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>');
  return ctx;
}
